document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const formatMoney = (cents) => {
  try {
    const amount = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
    return `$${amount}`;
  } catch (_error) {
    return `$${(cents / 100).toFixed(2)}`;
  }
};

const initReveal = () => {
  const elements = document.querySelectorAll('[data-reveal]');
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    elements.forEach((element) => element.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
  );
  elements.forEach((element) => observer.observe(element));
};

const initHeader = () => {
  const header = document.querySelector('[data-site-header]');
  if (!header) return;
  const setSticky = () => header.classList.toggle('is-sticky', window.scrollY > 48);
  setSticky();
  window.addEventListener('scroll', setSticky, { passive: true });

  const menu = header.querySelector('[data-site-menu]');
  const toggle = header.querySelector('[data-menu-toggle]');
  const panel = header.querySelector('[data-menu-panel]');
  if (!menu || !toggle || menu.dataset.initialized) return;
  menu.dataset.initialized = 'true';

  const randomizeMenuProducts = () => {
    const products = [...menu.querySelectorAll('[data-menu-product]')];
    if (!products.length) return;
    const shuffled = products.slice();
    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
    }
    const selected = new Set(shuffled.slice(0, Math.min(2, shuffled.length)));
    products.forEach((product) => { product.hidden = !selected.has(product); });
  };

  const syncMenuState = (isOpen) => {
    toggle.setAttribute('aria-label', isOpen ? 'Cerrar menú de navegación' : 'Abrir menú de navegación');
    toggle.setAttribute('aria-expanded', String(isOpen));
    document.body.classList.toggle('menu-is-open', isOpen);
    header.classList.toggle('menu-open', isOpen);
    panel?.toggleAttribute('inert', !isOpen);
    panel?.setAttribute('aria-hidden', String(!isOpen));
  };

  const closeMenu = ({ restoreFocus = true } = {}) => {
    const wasOpen = menu.open;
    menu.open = false;
    syncMenuState(false);
    if (wasOpen && restoreFocus) window.requestAnimationFrame(() => toggle.focus());
  };

  menu.addEventListener('toggle', () => {
    const isOpen = menu.open;
    syncMenuState(isOpen);
    if (isOpen) {
      randomizeMenuProducts();
      window.requestAnimationFrame(() => menu.querySelector('.site-menu__navigation a[href]')?.focus());
    }
  });
  menu.querySelector('[data-menu-refresh]')?.addEventListener('click', randomizeMenuProducts);
  randomizeMenuProducts();
  menu.querySelector('[data-menu-close]')?.addEventListener('click', closeMenu);
  menu.addEventListener('click', (event) => {
    if (event.target.closest('a[href]')) closeMenu({ restoreFocus: false });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && menu.open) {
      closeMenu();
    }
    if (event.key === 'Tab' && menu.open) {
      const focusable = [...menu.querySelectorAll('summary, a[href], button:not([disabled])')]
        .filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });
  document.addEventListener('click', (event) => {
    if (menu.open && !menu.contains(event.target)) closeMenu();
  });

  const resetMenuState = () => closeMenu({ restoreFocus: false });
  window.addEventListener('pagehide', resetMenuState);
  window.addEventListener('pageshow', resetMenuState);
  resetMenuState();
};

const initAutoplayVideos = () => {
  document.querySelectorAll('[data-autoplay-video]').forEach((video) => {
    video.muted = true;
    video.defaultMuted = true;
    const playVideo = () => video.play().catch(() => {});
    if (video.readyState >= 2) playVideo();
    else video.addEventListener('canplay', playVideo, { once: true });
  });
};

const initSort = () => {
  document.querySelectorAll('[data-sort-select]').forEach((select) => {
    select.addEventListener('change', () => select.form.submit());
  });
};

const initInfiniteCollection = () => {
  document.querySelectorAll('[data-infinite-collection]').forEach((collection) => {
    if (collection.dataset.initialized) return;
    const grid = collection.querySelector('[data-product-grid]');
    const loader = collection.querySelector('[data-collection-load-more]');
    const status = loader?.querySelector('[data-collection-status]');
    const retryButton = loader?.querySelector('[data-collection-retry]');
    if (!grid || !loader || !status || !loader.dataset.nextUrl) return;
    collection.dataset.initialized = 'true';

    let nextUrl = new URL(loader.dataset.nextUrl, window.location.origin);
    let isLoading = false;
    let hasFailed = false;
    let observer;

    const setStatus = (message, isVisible = true) => {
      status.textContent = message;
      status.hidden = !isVisible;
    };

    const finishLoading = (message) => {
      observer?.disconnect();
      loader.dataset.complete = 'true';
      if (retryButton) retryButton.hidden = true;
      setStatus(message);
    };

    const loadNextPage = async () => {
      if (isLoading || hasFailed || loader.dataset.complete === 'true') return;
      isLoading = true;
      loader.dataset.loading = 'true';
      grid.setAttribute('aria-busy', 'true');
      setStatus('Cargando más piezas…');

      try {
        const response = await fetch(nextUrl.href, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error('No se pudo cargar la siguiente página');

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = documentFragment.querySelector('[data-product-grid]');
        if (!nextGrid) throw new Error('No se encontró el catálogo');

        const cards = [...nextGrid.children].filter((card) => card.matches('.product-card'));
        cards.forEach((card) => grid.appendChild(card));
        initReveal();
        initAjaxCart();

        const nextLoader = documentFragment.querySelector('[data-collection-load-more]');
        const upcomingUrl = nextLoader?.dataset.nextUrl;
        if (cards.length === 0 || !upcomingUrl) {
          finishLoading('Has llegado al final del catálogo.');
        } else {
          nextUrl = new URL(upcomingUrl, window.location.origin);
          loader.dataset.nextUrl = nextUrl.href;
          status.setAttribute('role', 'status');
          status.hidden = true;
        }
      } catch (_error) {
        hasFailed = true;
        status.setAttribute('role', 'alert');
        setStatus('No pudimos cargar más piezas. Intenta de nuevo.');
        if (retryButton) retryButton.hidden = false;
      } finally {
        isLoading = false;
        loader.dataset.loading = 'false';
        grid.setAttribute('aria-busy', 'false');
      }
    };

    retryButton?.addEventListener('click', () => {
      if (!hasFailed || isLoading) return;
      hasFailed = false;
      retryButton.hidden = true;
      status.setAttribute('role', 'status');
      loadNextPage();
    });

    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadNextPage();
      },
      { rootMargin: '0px 0px 600px 0px' },
    );
    observer.observe(loader);
  });
};

const escapeHtml = (value) => String(value)
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#039;');

const initPredictiveSearch = () => {
  document.querySelectorAll('[data-predictive-search]').forEach((form) => {
    if (form.dataset.initialized) return;
    const input = form.querySelector('[data-predictive-search-input]');
    const results = form.querySelector('[data-predictive-search-results]');
    if (!input || !results) return;
    form.dataset.initialized = 'true';

    const instanceId = Math.random().toString(36).slice(2, 8);

    let controller;
    let debounceTimer;
    let activeIndex = -1;
    let requestSequence = 0;

    const closeResults = () => {
      results.hidden = true;
      results.innerHTML = '';
      form.classList.remove('has-predictive-results');
      form.closest('[data-search-overlay]')?.classList.remove('has-predictive-results');
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
    };

    const getOptions = () => [...results.querySelectorAll('[role="option"]')];

    const setActiveOption = (index) => {
      const options = getOptions();
      if (!options.length) return;
      activeIndex = (index + options.length) % options.length;
      options.forEach((option, optionIndex) => option.setAttribute('aria-selected', String(optionIndex === activeIndex)));
      input.setAttribute('aria-activedescendant', options[activeIndex].id);
    };

    const renderResults = (data, query) => {
      const found = data.resources?.results || {};
      const groups = [
        ['Piezas', found.products || []],
        ['Colecciones', found.collections || []],
        ['Páginas', found.pages || []],
        ['Historias', found.articles || []],
      ];
      const entries = groups.flatMap(([type, items]) => items.map((item) => ({ type, item }))).slice(0, 8);
      const productEntries = entries.filter(({ type }) => type === 'Piezas');
      const otherEntries = entries.filter(({ type }) => type !== 'Piezas');

      if (!entries.length) {
        results.innerHTML = `<p class="predictive-search__status">No encontramos piezas para “${escapeHtml(query)}”. Prueba con otra palabra o explora las sugerencias.</p>`;
        results.hidden = false;
        form.classList.add('has-predictive-results');
        form.closest('[data-search-overlay]')?.classList.add('has-predictive-results');
        input.setAttribute('aria-expanded', 'true');
        return;
      }

      const productOptions = productEntries.map(({ type, item }, index) => {
        const image = item.featured_image?.url || item.image?.url;
        const imageAlt = item.featured_image?.alt || item.image?.alt || item.title;
        return `<a class="predictive-search__card" id="PredictiveSearchOption-${instanceId}-${index}" href="${escapeHtml(item.url)}" role="option" aria-selected="false">
          <span class="predictive-search__image">${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(imageAlt)}" loading="lazy" width="480" height="640">` : '<span aria-hidden="true"></span>'}</span>
          <span class="predictive-search__card-copy"><span class="predictive-search__type">${type}</span><span>${escapeHtml(item.title)}</span></span>
        </a>`;
      }).join('');
      const otherOptions = otherEntries.map(({ type, item }, index) => `
        <li class="predictive-search__item">
          <a class="predictive-search__link" id="PredictiveSearchOption-${instanceId}-${productEntries.length + index}" href="${escapeHtml(item.url)}" role="option" aria-selected="false">
            <span>${escapeHtml(item.title)}</span><span class="predictive-search__type">${type}</span>
          </a>
        </li>`).join('');
      const searchUrl = new URL(form.action, window.location.origin);
      searchUrl.searchParams.set('q', query);
      results.innerHTML = `<div class="predictive-search__header"><p class="predictive-search__status">${entries.length} resultado${entries.length === 1 ? '' : 's'} para “${escapeHtml(query)}”</p><a class="text-link text-link--small predictive-search__all" href="${escapeHtml(searchUrl.pathname + searchUrl.search)}">Ver todos <svg class="icon" aria-hidden="true" viewBox="0 0 24 24" fill="none"><path d="M4 12h15M14 6l6 6-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></a></div>${productOptions ? `<div class="predictive-search__grid" role="listbox">${productOptions}</div>` : ''}${otherOptions ? `<ul class="predictive-search__list" role="listbox">${otherOptions}</ul>` : ''}`;
      results.hidden = false;
      form.classList.add('has-predictive-results');
      form.closest('[data-search-overlay]')?.classList.add('has-predictive-results');
      input.setAttribute('aria-expanded', 'true');
    };

    const requestResults = async (query, requestId) => {
      controller?.abort();
      controller = new AbortController();
      const endpoint = new URL(`${window.Shopify?.routes?.root || '/'}search/suggest.json`, window.location.origin);
      endpoint.searchParams.set('q', query);
      endpoint.searchParams.set('resources[type]', 'product,collection,page,article');
      endpoint.searchParams.set('resources[limit]', '8');
      endpoint.searchParams.set('resources[options][unavailable_products]', 'hide');

      try {
        const response = await fetch(endpoint, { headers: { Accept: 'application/json' }, signal: controller.signal });
        if (!response.ok) throw new Error('No se pudieron obtener las sugerencias');
        const data = await response.json();
        if (requestId !== requestSequence || input.value.trim() !== query) return;
        renderResults(data, query);
      } catch (error) {
        if (error.name !== 'AbortError') closeResults();
      }
    };

    input.addEventListener('input', () => {
      const query = input.value.trim();
      const requestId = ++requestSequence;
      window.clearTimeout(debounceTimer);
      controller?.abort();
      closeResults();
      if (query.length < 2) {
        return;
      }
      debounceTimer = window.setTimeout(() => requestResults(query, requestId), 220);
    });

    input.addEventListener('keydown', (event) => {
      const options = getOptions();
      if (event.key === 'ArrowDown' && options.length) {
        event.preventDefault();
        setActiveOption(activeIndex + 1);
      } else if (event.key === 'ArrowUp' && options.length) {
        event.preventDefault();
        setActiveOption(activeIndex - 1);
      } else if (event.key === 'Enter' && activeIndex >= 0 && options[activeIndex]) {
        event.preventDefault();
        options[activeIndex].click();
      } else if (event.key === 'Escape') {
        closeResults();
      }
    });

    document.addEventListener('click', (event) => {
      if (!form.contains(event.target)) closeResults();
    });
  });
};

const initSearchOverlay = () => {
  const overlay = document.querySelector('[data-search-overlay]');
  const dialog = overlay?.querySelector('[data-search-dialog]');
  const input = overlay?.querySelector('[data-predictive-search-input]');
  const triggers = [...document.querySelectorAll('[data-search-trigger]')];
  if (!overlay || !dialog || !input || !triggers.length || overlay.dataset.initialized) return;
  overlay.dataset.initialized = 'true';

  let returnFocus;
  let closeTimer;

  const setOpen = (isOpen, trigger) => {
    window.clearTimeout(closeTimer);
    if (isOpen) {
      returnFocus = trigger || document.activeElement;
      overlay.hidden = false;
      overlay.inert = false;
      overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('search-is-open');
      triggers.forEach((item) => item.setAttribute('aria-expanded', 'true'));
      window.requestAnimationFrame(() => overlay.classList.add('is-active'));
      window.setTimeout(() => input.focus(), 40);
      return;
    }

    overlay.classList.remove('is-active');
    input.value = '';
    input.dispatchEvent(new Event('input'));
    overlay.inert = true;
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('search-is-open');
    triggers.forEach((item) => item.setAttribute('aria-expanded', 'false'));
    closeTimer = window.setTimeout(() => { overlay.hidden = true; }, 240);
    returnFocus?.focus?.();
  };

  const resetSearchState = () => {
    window.clearTimeout(closeTimer);
    overlay.classList.remove('is-active');
    overlay.hidden = true;
    overlay.inert = true;
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('search-is-open');
    triggers.forEach((item) => item.setAttribute('aria-expanded', 'false'));
    returnFocus = null;
  };

  triggers.forEach((trigger) => trigger.addEventListener('click', () => setOpen(true, trigger)));
  overlay.querySelectorAll('[data-search-close]').forEach((close) => close.addEventListener('click', () => setOpen(false)));
  overlay.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      setOpen(false);
    }
  });
  dialog.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const focusable = [...dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])')].filter((item) => !item.hidden);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  window.addEventListener('pagehide', resetSearchState);
  window.addEventListener('pageshow', resetSearchState);
  resetSearchState();
};

const initQuantityControls = () => {
  document.querySelectorAll('.quantity-control').forEach((control) => {
    const input = control.querySelector('input');
    if (!input) return;
    const minus = control.querySelector('[data-quantity-minus]');
    const plus = control.querySelector('[data-quantity-plus]');
    const clamp = () => {
      const min = Number(input.min || 1);
      const max = input.max ? Math.max(min, Number(input.max)) : Number.POSITIVE_INFINITY;
      input.value = Math.min(max, Math.max(min, Number(input.value || min)));
      minus.disabled = Number(input.value) <= min;
      plus.disabled = Number(input.value) >= max;
    };
    minus?.addEventListener('click', () => {
      input.value = Number(input.value || 1) - 1;
      clamp();
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    plus?.addEventListener('click', () => {
      input.value = Number(input.value || 1) + 1;
      clamp();
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    input.addEventListener('input', clamp);
    input.addEventListener('change', clamp);
    clamp();
  });
};

const initProductForms = () => {
  document.querySelectorAll('[data-product-form]').forEach((form) => {
    const variantsNode = form.querySelector('[data-product-json]');
    if (!variantsNode) return;
    const inventoryNode = form.querySelector('[data-variant-inventory]');
    const inventoryById = new Map((inventoryNode ? JSON.parse(inventoryNode.textContent) : [])
      .map((inventory) => [String(inventory.id), inventory]));
    const variants = JSON.parse(variantsNode.textContent).map((variant) => ({
      ...variant,
      ...inventoryById.get(String(variant.id)),
    }));
    const idInput = form.querySelector('[data-variant-id]');
    const addButton = form.querySelector('[data-add-to-cart]');
    const price = form.closest('.product-info')?.querySelector('[data-price]');
    const priceRange = price?.dataset.priceVaries === 'true' ? price.querySelector('.price__current')?.textContent.trim() : null;
    const status = form.querySelector('[data-variant-status]');
    const quantityInput = form.querySelector('input[name="quantity"]');
    const quantityButtons = [...form.querySelectorAll('.quantity-control button')];
    const pickers = [...form.querySelectorAll('.variant-picker')];
    const presaleProperties = [...form.querySelectorAll('[data-presale-property]')];
    const presaleNote = form.querySelector('[data-presale-note]');
    const presaleDetails = form.closest('.product-info')?.querySelector('[data-presale-details]');
    const allowsPresale = form.classList.contains('product-form--presale');
    const isPresale = (variant) => allowsPresale && variant?.inventory_policy === 'continue';
    const canOrder = (variant) => Boolean(variant?.available);

    const updatePresaleContent = (variant) => {
      const presale = canOrder(variant) && isPresale(variant);
      presaleProperties.forEach((input) => { input.disabled = !presale; });
      if (presaleNote) presaleNote.hidden = !presale;
      if (presaleDetails) presaleDetails.hidden = !presale;
    };

    const getOptions = () => pickers.map((picker) => picker.querySelector('input:checked')?.value);
    const matchesOtherOptions = (variant, selected, ignoredIndex) => variant.options.every((option, index) => index === ignoredIndex || option === selected[index]);
    const updateOptionAvailability = (selected) => {
      pickers.forEach((picker, pickerIndex) => {
        picker.querySelectorAll('input[type="radio"]').forEach((input) => {
          const available = variants.some((variant) => canOrder(variant)
            && variant.options[pickerIndex] === input.value
            && matchesOtherOptions(variant, selected, pickerIndex));
          input.disabled = !available;
          input.nextElementSibling?.setAttribute('aria-disabled', String(!available));
        });
      });
    };

    const chooseAvailableOptions = () => {
      const selected = getOptions();
      let changed = false;
      pickers.forEach((picker, pickerIndex) => {
        const current = picker.querySelector('input:checked');
        const available = [...picker.querySelectorAll('input[type="radio"]')].filter((input) => variants.some((variant) => canOrder(variant)
          && variant.options[pickerIndex] === input.value
          && matchesOtherOptions(variant, selected, pickerIndex)));
        if (current && !current.disabled && available.includes(current)) return;
        if (available[0] && available[0] !== current) {
          available[0].checked = true;
          changed = true;
        }
      });
      return changed;
    };

    const updateQuantityLimit = (variant) => {
      const tracked = variant && variant.inventory_management && variant.inventory_policy !== 'continue' && Number.isFinite(Number(variant.inventory_quantity));
      if (quantityInput) {
        if (tracked) {
          quantityInput.max = String(Math.max(1, Number(variant.inventory_quantity)));
        } else {
          quantityInput.removeAttribute('max');
        }
        quantityInput.value = String(Math.min(Number(quantityInput.max || Number.POSITIVE_INFINITY), Math.max(1, Number(quantityInput.value || 1))));
      }
      const value = Number(quantityInput?.value || 1);
      const max = quantityInput?.max ? Number(quantityInput.max) : Number.POSITIVE_INFINITY;
      quantityButtons.forEach((button) => {
        const atLimit = button.hasAttribute('data-quantity-minus') ? value <= 1 : value >= max;
        button.disabled = !canOrder(variant) || atLimit;
      });
    };

    const updateStatus = (variant) => {
      if (!status) return;
      if (!variant) {
        status.textContent = 'Selecciona una combinación disponible.';
      } else if (!canOrder(variant)) {
        status.textContent = 'Agotado en esta talla.';
      } else if (isPresale(variant)) {
        status.textContent = 'Disponible para preventa';
      } else if (variant.inventory_management && Number.isFinite(Number(variant.inventory_quantity))) {
        const count = Number(variant.inventory_quantity);
        status.textContent = `${count} ${count === 1 ? 'pieza disponible' : 'piezas disponibles'}`;
      } else {
        status.textContent = 'Disponible';
      }
      status.hidden = false;
    };

    const updateVariant = () => {
      let options = getOptions();
      for (let attempt = 0; attempt <= pickers.length; attempt += 1) {
        updateOptionAvailability(options);
        if (!chooseAvailableOptions()) break;
        options = getOptions();
      }
      updateOptionAvailability(options);
      const variant = variants.find((item) => pickers.length === 0 || item.options.every((option, index) => option === options[index]));
      if (!variant) {
        addButton.disabled = true;
        addButton.textContent = 'Combinación no disponible';
        updatePresaleContent(null);
        updateQuantityLimit(null);
        updateStatus(null);
        return;
      }
      idInput.value = variant.id;
      addButton.disabled = !canOrder(variant);
      addButton.textContent = canOrder(variant) ? (isPresale(variant) ? 'Comprar en preventa' : 'Agregar al carrito') : 'Agotado';
      updatePresaleContent(variant);
      updateQuantityLimit(variant);
      updateStatus(variant);
      if (price) {
        const onSale = variant.compare_at_price > variant.price;
        price.classList.toggle('price--sale', onSale);
        price.innerHTML = `<span class="price__current">${formatMoney(variant.price)}</span>${onSale ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>` : ''}${priceRange ? `<span class="price__range">Rango: ${priceRange}</span>` : ''}`;
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    };

    form.querySelectorAll('.variant-picker input').forEach((input) => input.addEventListener('change', updateVariant));
    updateVariant();
  });
};

const initProductImageZoom = () => {
  document.querySelectorAll('[data-product-image-zoom]').forEach((button) => {
    if (button.dataset.productImageZoomInitialized) return;
    const item = button.closest('.product-gallery__item');
    if (!item) return;

    button.dataset.productImageZoomInitialized = 'true';
    const label = button.dataset.zoomLabel || 'la imagen';
    const setOrigin = (event) => {
      const bounds = button.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, ((event.clientX - bounds.left) / bounds.width) * 100));
      const y = Math.min(100, Math.max(0, ((event.clientY - bounds.top) / bounds.height) * 100));
      button.style.setProperty('--zoom-x', `${x}%`);
      button.style.setProperty('--zoom-y', `${y}%`);
    };
    const setZoom = (isZoomed) => {
      item.classList.toggle('is-zoomed', isZoomed);
      button.setAttribute('aria-pressed', String(isZoomed));
      button.setAttribute('aria-label', `${isZoomed ? 'Alejar' : 'Acercar'} ${label}`);
    };

    button.addEventListener('pointermove', setOrigin);
    button.addEventListener('pointerleave', () => {
      if (!item.classList.contains('is-zoomed')) {
        button.style.setProperty('--zoom-x', '50%');
        button.style.setProperty('--zoom-y', '50%');
      }
    });
    button.addEventListener('click', (event) => {
      if (event.detail) setOrigin(event);
      else {
        button.style.setProperty('--zoom-x', '50%');
        button.style.setProperty('--zoom-y', '50%');
      }
      setZoom(!item.classList.contains('is-zoomed'));
    });
    button.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && item.classList.contains('is-zoomed')) setZoom(false);
    });
  });
};

const showToast = (message) => {
  const toast = document.querySelector('[data-cart-toast]');
  if (!toast) return;
  toast.textContent = message;
  toast.classList.add('is-visible');
  window.setTimeout(() => toast.classList.remove('is-visible'), 2800);
};

const initAjaxCart = () => {
  document.querySelectorAll('form[data-ajax-cart]').forEach((form) => {
    if (form.dataset.initialized) return;
    form.dataset.initialized = 'true';
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = form.querySelector('button[type="submit"]');
      button.disabled = true;
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        if (!response.ok) throw new Error('No se pudo agregar la pieza');
        const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((result) => result.json());
        document.querySelectorAll('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count; });
        showToast('La pieza se agregó a tu carrito.');
      } catch (error) {
        showToast(error.message);
      } finally {
        button.disabled = false;
      }
    });
  });
};

const init = () => {
  initReveal();
  initHeader();
  initAutoplayVideos();
  initSort();
  initInfiniteCollection();
  initPredictiveSearch();
  initSearchOverlay();
  initQuantityControls();
  initProductForms();
  initProductImageZoom();
  initAjaxCart();
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

document.addEventListener('shopify:section:load', init);
